import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDashboardState } from './useDashboardState';
import { useDashboardHandlers } from './useDashboardHandlers';
import { useUrlParams } from './useUrlParams';
import { DashboardHeader } from './DashboardHeader';
import { SitesHeader } from './SitesHeader';
import { CreateSiteForm } from './CreateSiteForm';
import { SiteCard } from './SiteCard';
import { EmptyState } from './EmptyState';
import { SuccessToast } from './SuccessToast';
import UsageLimits from '../../components/UsageLimits';
import { SiteTemplateLibraryModal } from '../../components/SiteTemplateLibraryModal';
import { DeleteSiteModal } from '../../components/DeleteSiteModal';
import { RoaryEntryCard, RoaryMode } from '../../components/Roary';
import { canUseRoary } from '../../utils/roaryAccess';
import { GettingStartedHost } from '../../components/GettingStarted';

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, isAdmin, isMimicking, loading: authLoading, logout } = useAuth();
  const [showRoary, setShowRoary] = useState(false);
  const [roaryOpened, setRoaryOpened] = useState(false);
  const showRoaryUi = canUseRoary(isAdmin, isMimicking);

  const state = useDashboardState();
  const handlers = useDashboardHandlers({ state, logout });

  useEffect(() => {
    localStorage.removeItem('demoPlan');
  }, []);

  // Handle URL parameters for messages
  useUrlParams(state);

  // Auto-open Roary when an admin lands here via the "Build with Roary" entry
  // (which navigates to /dashboard?roary=open after mimicking the customer).
  useEffect(() => {
    if (searchParams.get('roary') === 'open' && showRoaryUi) {
      setShowRoary(true);
      const next = new URLSearchParams(searchParams);
      next.delete('roary');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, showRoaryUi]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated) {
      handlers.loadSites();
      handlers.loadCurrentPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, isAuthenticated, authLoading]);

  const handleShowCreateForm = () => {
    if (
      state.currentPlan &&
      state.currentPlan.maxSites &&
      state.sites.length >= state.currentPlan.maxSites
    ) {
      state.setCreateError(
        `You've reached the maximum number of websites (${state.currentPlan.maxSites}) for your ${state.currentPlan.displayName || 'current'} plan. Please upgrade to create more websites.`
      );
      state.setShowSuccessToast(true);
      state.setSuccessMessage(
        `You've reached the maximum number of websites (${state.currentPlan.maxSites}) for your ${state.currentPlan.displayName || 'current'} plan. Please upgrade to create more websites.`
      );
      setTimeout(() => state.setShowSuccessToast(false), 5000);
      return;
    }
    state.setShowCreateForm(true);
  };

  // Open create form when arriving from Get Started on another surface.
  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    if (authLoading || state.isLoading || !isAuthenticated) return;
    handleShowCreateForm();
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, authLoading, state.isLoading, isAuthenticated]);

  if (authLoading || state.isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading your websites...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={`page-container ${isMimicking ? 'mt-[44px]' : ''}`}>
      <DashboardHeader
        userName={user?.name}
        userEmail={user?.email}
        currentPlan={state.currentPlan}
        subscriptionStatus={state.subscriptionStatus}
        isAdmin={isAdmin}
        onLogout={handlers.handleLogout}
      />

      <main className="page-content max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="mb-8">
            <SitesHeader
              sites={state.sites}
              currentPlan={state.currentPlan}
              showCreateForm={state.showCreateForm}
              onShowCreateForm={handleShowCreateForm}
            />

            <div className="animate-slide-up">
              <UsageLimits />
            </div>
          </div>

          {state.showCreateForm && (
            <CreateSiteForm
              newSiteName={state.newSiteName}
              selectedTheme={state.selectedTheme}
              useTemplate={state.useTemplate}
              selectedTemplate={state.selectedTemplate}
              isCreating={state.isCreating}
              createError={state.createError}
              onNameChange={state.setNewSiteName}
              onThemeChange={state.setSelectedTheme}
              onUseTemplateChange={use => {
                state.setUseTemplate(use);
                if (!use) {
                  state.setSelectedTemplate(null);
                  state.setNewSiteName('');
                }
              }}
              onShowTemplateLibrary={() => state.setShowTemplateLibrary(true)}
              onSubmit={handlers.handleCreateSite}
              onCancel={handlers.resetCreateForm}
            />
          )}

          {state.sites.length === 0 && !state.showCreateForm ? (
            <EmptyState
              currentPlan={state.currentPlan}
              sitesCount={state.sites.length}
              onCreateWebsite={handleShowCreateForm}
            />
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 stagger-children">
              {state.sites.map(site => (
                <SiteCard key={site.id} site={site} onDelete={handlers.handleDeleteSite} />
              ))}
            </div>
          )}

          <RoaryEntryCard
            onStart={() => {
              setRoaryOpened(true);
              setShowRoary(true);
            }}
          />
        </div>
      </main>

      <SiteTemplateLibraryModal
        isOpen={state.showTemplateLibrary}
        onClose={() => state.setShowTemplateLibrary(false)}
        onSelectTemplate={handlers.handleTemplateSelect}
        isAdmin={isAdmin}
        currentUserId={user?.id}
      />

      <DeleteSiteModal
        isOpen={state.showDeleteModal}
        onClose={handlers.handleDeleteCancel}
        onConfirm={handlers.handleDeleteConfirm}
        isLoading={state.isDeleting}
        siteName={state.siteToDelete?.name || ''}
        errorMessage={state.deleteError}
      />

      <SuccessToast
        show={state.showSuccessToast}
        message={state.successMessage}
        onClose={() => state.setShowSuccessToast(false)}
      />

      <GettingStartedHost sites={state.sites} onCreateWebsite={handleShowCreateForm} />

      {(showRoaryUi || roaryOpened) && (
        <RoaryMode
          isOpen={showRoary}
          onClose={() => setShowRoary(false)}
          onSiteCreated={handlers.loadSites}
          allowImport
          importOnly={!showRoaryUi}
        />
      )}
    </div>
  );
};

export default Dashboard;
